/**
 * 地图工具类 - 用于处理桥梁监测系统中的地图相关功能
 */

import L from 'leaflet';
import { formatLatitude, formatLongitude } from './coordinate';

/**
 * 计算两个地理坐标点之间的距离（使用Haversine公式）
 * @param {number} lat1 - 第一个点的纬度
 * @param {number} lng1 - 第一个点的经度
 * @param {number} lat2 - 第二个点的纬度
 * @param {number} lng2 - 第二个点的经度
 * @returns {number} 距离（公里）
 */
export function calculateGeoDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // 地球半径（公里）
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * 修复Leaflet默认图标问题
 */
export function fixDefaultLeafletIcons() {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
  });
}

/**
 * 区域坐标解析 - 将存储的区域坐标转换为Leaflet可用的格式
 * @param {Object} area - 区域对象，包含coordinates字段和geometryType字段
 * @returns {Array} - 格式化后的坐标数组，适用于Leaflet
 */
export function parseAreaCoordinates(area) {
  try {
    if (!area) {
      return [];
    }
    
    // 获取几何类型，默认为POLYGON
    const geometryType = area.geometryType || 'POLYGON';
    
    // 处理直接传递坐标数组的情况
    if (Array.isArray(area)) {
      // 对于点标注，只需要一个坐标点
      if (area.length === 2 && typeof area[0] === 'number' && typeof area[1] === 'number') {
        // 单个点坐标：[lat, lng]
        return [area];
      }
      
      // 对于点标注几何类型，即使传入多个点也只取第一个
      if (geometryType === 'POINT' && area.length >= 1) {
        const firstPoint = area[0];
        if (Array.isArray(firstPoint) && firstPoint.length >= 2) {
          return [firstPoint];
        }
      }
      
      // 检查数组是否至少有2个元素（线段和多边形），否则返回空数组
      if (area.length < 2) {
        return [];
      }
      
      // 检查每个点是否有效
      let validArray = true;
      const result = area.map(point => {
        if (Array.isArray(point) && point.length >= 2) {
          return [Number(point[0]), Number(point[1])];
        } else if (point && typeof point === 'object' && 'lat' in point && 'lng' in point) {
          return [Number(point.lat), Number(point.lng)];
        } else {
          validArray = false;
          return [0, 0];
        }
      });
      
      return validArray ? result : [];
    }
    
    // 处理没有坐标数据的情况
    if (!area.coordinates) {
      return [];
    }
    
    // 如果已经是数组，直接使用
    if (Array.isArray(area.coordinates)) {
      // 对于点标注，只需要一个坐标点
      if (geometryType === 'POINT' && area.coordinates.length >= 2 && 
          typeof area.coordinates[0] === 'number' && typeof area.coordinates[1] === 'number') {
        // 单个点坐标：[lat, lng]
        return [area.coordinates];
      }
      
      // 对于点标注几何类型，即使是嵌套数组也只取第一个点
      if (geometryType === 'POINT' && area.coordinates.length >= 1 && Array.isArray(area.coordinates[0])) {
        const firstPoint = area.coordinates[0];
        if (firstPoint.length >= 2) {
          return [firstPoint];
        }
      }
      
      // 验证数组内容是否有效（线段和多边形至少需要2个点）
      if (geometryType !== 'POINT' && area.coordinates.length < 2) {
        return [];
      }
      
      // 检查每个点的格式
      const firstPoint = area.coordinates[0];
      if (Array.isArray(firstPoint) && firstPoint.length >= 2) {
        // 标准格式：[[lat1,lng1], [lat2,lng2], ...]
        return area.coordinates;
      } else if (firstPoint && typeof firstPoint === 'object' && 'lat' in firstPoint && 'lng' in firstPoint) {
        // 对象格式：[{lat:1,lng:2}, {lat:3,lng:4}, ...]
        return area.coordinates.map(point => [point.lat, point.lng]);
      } else {
        return [];
      }
    }
    
    // 尝试解析坐标JSON
    let coordsData;
    
    if (typeof area.coordinates === 'string') {
      try {
        coordsData = JSON.parse(area.coordinates);
      } catch (parseError) {
        console.error('区域坐标JSON解析失败');
        return [];
      }
    } else {
      coordsData = area.coordinates;
    }
    
    // 数据为空的情况
    if (!coordsData) {
      return [];
    }
    
    // 特殊处理点标注类型
    if (geometryType === 'POINT') {
      // 点标注坐标格式：[lat, lng]
      if (Array.isArray(coordsData) && coordsData.length === 2 && 
          typeof coordsData[0] === 'number' && typeof coordsData[1] === 'number') {
        return [coordsData]; // 返回包含单个点的数组
      }
      // 如果是嵌套格式的点坐标，尝试提取
      if (Array.isArray(coordsData) && coordsData.length > 0 && Array.isArray(coordsData[0])) {
        const point = coordsData[0];
        if (point.length === 2 && typeof point[0] === 'number' && typeof point[1] === 'number') {
          return [point];
        }
      }
      console.warn('点标注坐标格式无法解析:', coordsData);
      return [];
    }
    
    // 特殊处理圆形类型（解析两点格式的圆形数据）
    if (geometryType === 'CIRCLE') {
      // 圆形坐标格式：[[centerLng,centerLat],[edgeLng,edgeLat]] 或 [[centerLat,centerLng],[edgeLat,edgeLng]]
      if (Array.isArray(coordsData) && coordsData.length === 2) {
        const point1 = coordsData[0];
        const point2 = coordsData[1];
        
        if (Array.isArray(point1) && Array.isArray(point2) && 
            point1.length === 2 && point2.length === 2) {
          // 检查是否是GeoJSON格式（lng在前）还是标准格式（lat在前）
          // 简单判断：如果第一个值是经度范围(-180到180)且大于纬度范围，可能是GeoJSON格式
          const isGeoJSONFormat = Math.abs(point1[0]) > Math.abs(point1[1]) && Math.abs(point1[0]) <= 180;
          
          if (isGeoJSONFormat) {
            // GeoJSON格式：[lng, lat] -> 转换为 [lat, lng]
            return [[point1[1], point1[0]], [point2[1], point2[0]]];
          } else {
            // 标准格式：[lat, lng]
            return [point1, point2];
          }
        }
      }
      
      console.warn('圆形坐标格式无法解析:', coordsData);
      return [];
    }
    
    // 处理三层嵌套的标准格式 [[[lng1,lat1],[lng2,lat2],...]]
    if (Array.isArray(coordsData) && coordsData.length > 0) {
      try {
        // 检测是否为三层嵌套格式
        if (Array.isArray(coordsData[0]) && coordsData[0].length > 0) {
          if (Array.isArray(coordsData[0][0])) {
            // GeoJSON多边形格式: [[[lng1,lat1],[lng2,lat2],...]]
            
            // 获取坐标数组 (多边形的第一个环)
            let coords = coordsData[0];
            
            // 特殊情况处理：如果第一层只有一个元素且第二层也只有一个元素，尝试使用更深层次
            if (coordsData.length === 1 && Array.isArray(coordsData[0]) && coordsData[0].length === 1 && Array.isArray(coordsData[0][0])) {
              coords = coordsData[0][0];
            }
            
            // 确保至少有2个点才是有效线或区域
            if (coords.length < 2) {
              return [];
            }
            
            // 转换[lng,lat]为[lat,lng]用于Leaflet
            return coords.map(point => {
              if (Array.isArray(point) && point.length >= 2) {
                return [Number(point[1]), Number(point[0])]; // 交换经纬度顺序
              } else {
                return null;
              }
            }).filter(point => point !== null);
          } else if (typeof coordsData[0][0] === 'number') {
            // 简单二层格式：[[lng1,lat1], [lng2,lat2], ...]
            
            if (coordsData.length < 2) {
              return [];
            }
            
            return coordsData.map(point => {
              if (Array.isArray(point) && point.length >= 2) {
                return [Number(point[1]), Number(point[0])]; // 交换经纬度顺序
              } else {
                return null;
              }
            }).filter(point => point !== null);
          }
        }
        
        // 混合对象格式: [{lat,lng}, ...]
        if (coordsData.length > 0 && typeof coordsData[0] === 'object' && !Array.isArray(coordsData[0])) {
          
          if (coordsData.length < 2) {
            return [];
          }
          
          if ('lat' in coordsData[0] && 'lng' in coordsData[0]) {
            return coordsData.map(point => {
              if (point && typeof point === 'object' && 'lat' in point && 'lng' in point) {
                return [Number(point.lat), Number(point.lng)];
              } else {
                return null;
              }
            }).filter(point => point !== null);
          }
        }
      } catch (processingError) {
        console.error('处理坐标数据格式时出错');
      }
    }
    
    // 如果之前的处理都失败，尝试直接返回第一层
    if (Array.isArray(coordsData) && coordsData.length >= 2) {
      return coordsData;
    }
    
    // 控制台警告，但不再输出大量错误日志
    if (!window._loggedParseWarning) {
      window._loggedParseWarning = true;
      console.warn('区域坐标数据格式无法解析');
      
      // 每10秒后重置，允许再次输出警告
      setTimeout(() => {
        window._loggedParseWarning = false;
      }, 10000);
    }
    
    return [];
  } catch (error) {
    if (!window._loggedParseError) {
      window._loggedParseError = true;
      console.error('解析区域坐标失败');
      
      // 每10秒后重置，允许再次输出错误
      setTimeout(() => {
        window._loggedParseError = false;
      }, 10000);
    }
    
    return [];
  }
}

/**
 * 将圆形坐标转换为GeoJSON格式的坐标字符串
 * @param {Array} coordinates - 圆形坐标数组，格式为[[centerLat,centerLng],[edgeLat,edgeLng]]
 * @returns {String} - GeoJSON格式的坐标字符串（保持原始两点格式）
 */
export function formatCircleToGeoJson(coordinates) {
  if (!coordinates || !Array.isArray(coordinates) || coordinates.length < 2) {
    return JSON.stringify([[]]); // 返回空的GeoJSON坐标数组
  }
  
  try {
    const centerPoint = coordinates[0];
    const edgePoint = coordinates[1];
    
    if (!Array.isArray(centerPoint) || !Array.isArray(edgePoint) || 
        centerPoint.length < 2 || edgePoint.length < 2) {
      console.error('圆形坐标格式错误：需要两个有效的坐标点');
      return JSON.stringify([[]]);
    }
    
    // 对于圆形，我们直接保存两个点的坐标：[中心点, 边界点]
    // 转换[lat,lng]为[lng,lat]以符合GeoJSON规范
    const centerGeo = [Number(centerPoint[1]), Number(centerPoint[0])]; // [lng, lat]
    const edgeGeo = [Number(edgePoint[1]), Number(edgePoint[0])]; // [lng, lat]
    
    // 返回两点坐标数组，后端可通过geometryType="CIRCLE"识别这是圆形
    return JSON.stringify([centerGeo, edgeGeo]);
  } catch (error) {
    console.error('格式化圆形坐标出错:', error);
    return JSON.stringify([[]]);
  }
}

/**
 * 将坐标数组转换为GeoJSON格式的坐标字符串
 * @param {Array} coordinates - 坐标数组，格式为[[lat1,lng1],[lat2,lng2],...]
 * @param {Boolean} isLine - 是否为线条类型
 * @param {String} geometryType - 几何类型，可选值：'POLYGON', 'LINESTRING', 'RECTANGLE', 'CIRCLE'
 * @returns {String} - GeoJSON格式的坐标字符串
 */
export function formatCoordinatesToGeoJson(coordinates, isLine = false, geometryType = 'POLYGON') {
  if (!coordinates || !Array.isArray(coordinates) || coordinates.length === 0) {
    return JSON.stringify([[[]]]); // 返回空的GeoJSON坐标数组
  }
  
  // 特殊处理圆形类型
  if (geometryType === 'CIRCLE') {
    return formatCircleToGeoJson(coordinates);
  }
  
  try {
    // 确保输入坐标是标准格式 [[lat1,lng1],[lat2,lng2],...]
    let standardCoords = [...coordinates];
    
    // 验证坐标格式
    if (!Array.isArray(standardCoords[0])) {
      console.error('坐标格式错误：必须是二维数组');
      return JSON.stringify([[[]]]);
    }
    
    // 确保多边形首尾相连
    if (!isLine && standardCoords.length >= 3) {
      const firstPoint = standardCoords[0];
      const lastPoint = standardCoords[standardCoords.length - 1];
      
      // 如果首尾点不一致，添加首点到末尾
      if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
        standardCoords.push([...firstPoint]);
      }
    }
    
    // 转换[lat,lng]为[lng,lat]以符合GeoJSON规范
    const geoCoords = standardCoords.map(point => {
      // 确保每个点都是两个数字的数组
      if (Array.isArray(point) && point.length >= 2) {
        const lng = Number(point[1]); // 经度
        const lat = Number(point[0]); // 纬度
        
        if (isNaN(lng) || isNaN(lat)) {
          console.warn('无效的坐标值:', point);
          return [0, 0]; // 防止错误
        }
        
        return [lng, lat]; // 交换顺序：GeoJSON要求[经度,纬度]格式
      } else if (point && typeof point === 'object' && 'lat' in point && 'lng' in point) {
        const lng = Number(point.lng);
        const lat = Number(point.lat);
        
        if (isNaN(lng) || isNaN(lat)) {
          console.warn('无效的坐标对象值:', point);
          return [0, 0];
        }
        
        return [lng, lat]; // 对象格式
      } else {
        console.warn('无效坐标点:', point);
        return [0, 0]; // 防止错误
      }
    });
    
    // 对于区域(Polygon)类型，需要使用三层嵌套数组
    // 标准GeoJSON Polygon格式: [[[lng1,lat1],[lng2,lat2],...]]
    const result = JSON.stringify([geoCoords]);
    
    return result;
  } catch (error) {
    console.error('格式化坐标出错:', error);
    return JSON.stringify([[[]]]);
  }
}

/**
 * 获取区域颜色
 * @param {String} areaType - 区域类型代码
 * @returns {String} - 颜色代码
 */
export function getAreaColor(areaType) {
  if (!areaType) return '#95a5a6';
  
  const colorMap = {
    'REPORT_LINE_UP': '#3498db',     // 上游报告线 - 蓝色
    'REPORT_LINE_DOWN': '#2980b9',   // 下游报告线 - 深蓝色
    'WARNING_LINE_UP': '#e74c3c',    // 上游警戒线 - 红色
    'WARNING_LINE_DOWN': '#c0392b',  // 下游警戒线 - 深红色
    'NAVIGATION_UP': '#2ecc71',      // 上游通航区 - 绿色
    'NAVIGATION_DOWN': '#27ae60',    // 下游通航区 - 深绿色
    'CHANNEL_UP': '#9b59b6',         // 上游航道 - 紫色
    'CHANNEL_DOWN': '#8e44ad',       // 下游航道 - 深紫色
    'NO_NAVIGATION': '#e67e22',      // 禁航区 - 橙色
    'BRIDGE_MAST': '#f1c40f',        // 桥墩 - 黄色
    'RADAR_AREA_UP': '#1abc9c',      // 上游雷达区 - 青色
    'RADAR_AREA_DOWN': '#16a085',    // 下游雷达区 - 深青色
    'BRIDGE_LINE': '#34495e',        // 桥梁线 - 深灰色
    'BRIDGE': '#3498db',            // 桥梁区域 - 蓝色
    'WARNING': '#e74c3c',           // 警戒区域 - 红色
    'NAVIGATION': '#2ecc71',         // 通航区域 - 绿色
    'CHANNEL': '#9b59b6',           // 航道区域 - 紫色
    'RESTRICTED': '#e67e22',         // 限制区域 - 橙色
    'OTHER': '#95a5a6'              // 其他区域 - 灰色
  };
  
  return colorMap[areaType] || '#95a5a6'; // 默认颜色 - 灰色
}

/**
 * 安全地处理地图点击或移动事件，提取坐标
 * @param {Object} event - 地图事件对象
 * @returns {Object|null} - 格式化后的{lat, lng}对象或null
 */
export function extractCoordinatesFromMapEvent(event) {
  if (!event) return null;
  
  // 处理直接传递的坐标对象或包含latlng属性的事件对象
  let lat, lng;
  
  if (typeof event === 'object' && 'lat' in event && 'lng' in event) {
    // 直接是坐标对象的情况
    lat = event.lat;
    lng = event.lng;
    return { lat, lng };
  } else if (event.latlng && typeof event.latlng === 'object') {
    // 是事件对象的情况
    lat = event.latlng.lat;
    lng = event.latlng.lng;
    return { lat, lng };
  }
  
  return null; // 无法识别的格式
}

/**
 * 格式化坐标显示，保留6位小数
 * @param {Number} value - 坐标值
 * @returns {String} - 格式化后的坐标字符串
 */
export function formatCoordinate(value) {
  return value ? value.toFixed(6) : '0.000000';
}

/**
 * 获取当前时间字符串
 * @returns {String} - 当前时间的字符串表示 (yyyy-MM-dd HH:mm:ss)
 */
export function getCurrentTime() {
  return formatDate(new Date(), 'yyyy-MM-dd HH:mm:ss');
}

/**
 * 格式化日期
 * @param {Date} date - 日期对象
 * @param {String} fmt - 格式化模式字符串
 * @returns {String} - 格式化后的日期字符串
 */
export function formatDate(date, fmt) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }
  
  if (!fmt) {
    fmt = 'yyyy-MM-dd HH:mm:ss';
  }
  
  const o = {
    'M+': date.getMonth() + 1, // 月份
    'd+': date.getDate(), // 日
    'H+': date.getHours(), // 小时
    'h+': date.getHours() % 12 === 0 ? 12 : date.getHours() % 12, // 12小时制
    'm+': date.getMinutes(), // 分
    's+': date.getSeconds(), // 秒
    'q+': Math.floor((date.getMonth() + 3) / 3), // 季度
    'S': date.getMilliseconds() // 毫秒
  };
  
  if (/(y+)/.test(fmt)) {
    fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length));
  }
  
  for (let k in o) {
    if (new RegExp('(' + k + ')').test(fmt)) {
      fmt = fmt.replace(
        RegExp.$1,
        RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
      );
    }
  }
  
  return fmt;
}

/**
 * 计算两点之间的距离
 * @param {Number} lat1 - 第一个点的纬度
 * @param {Number} lng1 - 第一个点的经度
 * @param {Number} lat2 - 第二个点的纬度
 * @param {Number} lng2 - 第二个点的经度
 * @returns {Number} - 两点之间的距离
 */
export function calculateDistance(lat1, lng1, lat2, lng2) {
  return Math.sqrt(
    Math.pow(lat1 - lat2, 2) + 
    Math.pow(lng1 - lng2, 2)
  );
}

export default {
  fixDefaultLeafletIcons,
  parseAreaCoordinates,
  formatCoordinatesToGeoJson,
  formatCircleToGeoJson,
  getAreaColor,
  extractCoordinatesFromMapEvent,
  formatCoordinate,
  formatLatitude,
  formatLongitude,
  calculateDistance,
  calculateGeoDistance
}; 